import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { Card, Button, Badge, Spinner, EmptyState } from "../../components/ui";
import { AuthImg } from "../../components/AuthImg";
import { VehicleDiagram } from "../../components/VehicleDiagram";
import { ZONE_CODES } from "../../lib/vehicle";

type CheckStatus = "NOT_CHECKED" | "OK" | "OBSERVATION" | "DEFECT" | "NOT_APPLICABLE";
type Severity = "INFO" | "MINOR" | "MAJOR" | "CRITICAL";
interface Counts { total: number; ok: number; observation: number; defect: number; notApplicable: number; notChecked: number }
interface SnapCheck { key: string; label: string; unit: string | null; status: CheckStatus; value: Record<string, unknown> | null; comment: string | null; severity: Severity | null }
interface Snapshot {
  case: { publicRef: string };
  vehicle: { make: string; model: string; vin: string | null; mileageKm: number | null };
  inspection: {
    overallRecommendation: string | null; conclusion: string | null; counts: Counts;
    sections: { key: string; title: string; counts: Counts; checks: SnapCheck[] }[];
    findings: { vehicleArea: string | null; title: string; description: string | null; severity: Severity }[];
    media: { id: string; checkKey: string | null; kind: string; caption: string | null }[];
  } | null;
  signature: { signerName: string; signedAt: number } | null;
}

const REC: Record<string, { label: string; tone: "ok" | "warn" | "danger" }> = {
  RECOMMENDED: { label: "Recomendado", tone: "ok" },
  RECOMMENDED_WITH_OBSERVATIONS: { label: "Recomendado con observaciones", tone: "warn" },
  NOT_RECOMMENDED: { label: "No recomendado", tone: "danger" },
};
const ST_LABEL: Record<CheckStatus, string> = { OK: "Correcto", OBSERVATION: "Observación", DEFECT: "Defecto", NOT_APPLICABLE: "N/A", NOT_CHECKED: "No comprobado" };

function valueStr(c: SnapCheck): string {
  const v = c.value;
  if (!v) return "";
  if (typeof v.number === "number") return `${v.number}${c.unit ? ` ${c.unit}` : ""}`;
  if (typeof v.text === "string") return v.text;
  if (typeof v.option === "string") return v.option;
  return "";
}

export function PortalInspectionView() {
  const { id = "" } = useParams();
  const { data, loading, error } = useApi<Snapshot>(`/api/portal/cases/${id}/report/snapshot`);
  const [open, setOpen] = useState<string | null>(null);

  if (loading) return <Spinner />;
  if (error || !data?.inspection) return <EmptyState>El informe de inspección aún no está disponible.</EmptyState>;
  const insp = data.inspection;
  const rec = insp.overallRecommendation ? REC[insp.overallRecommendation] : null;
  const mediaByCheck = new Map<string, typeof insp.media>();
  for (const m of insp.media) if (m.checkKey) mediaByCheck.set(m.checkKey, [...(mediaByCheck.get(m.checkKey) ?? []), m]);

  return (
    <>
      <div className="mb-6">
        <Link to={`/cases/${id}`} className="text-sm text-muted hover:underline">← Expediente</Link>
        <h1 className="mt-2 text-2xl font-semibold">Inspección · {data.vehicle.make} {data.vehicle.model}</h1>
        <p className="text-sm text-muted">VIN {data.vehicle.vin ?? "—"} · {data.vehicle.mileageKm ?? "—"} km</p>
      </div>

      <Card className="mb-6 p-5">
        {rec && <div className="mb-3"><Badge tone={rec.tone}>{rec.label}</Badge></div>}
        <div className="grid grid-cols-3 gap-3 text-center sm:grid-cols-6">
          {[["Puntos", insp.counts.total], ["OK", insp.counts.ok], ["Obs.", insp.counts.observation], ["Defectos", insp.counts.defect], ["N/A", insp.counts.notApplicable], ["Sin compr.", insp.counts.notChecked]].map(([l, n]) => (
            <div key={l} className="rounded-lg bg-canvas p-2"><div className="text-xl font-semibold tnum">{n}</div><div className="text-xs text-muted">{l}</div></div>
          ))}
        </div>
        {insp.conclusion && <p className="mt-4 text-sm">{insp.conclusion}</p>}
        <div className="mt-4 flex items-center gap-3">
          <Button onClick={() => api.openTab(`/api/portal/cases/${id}/report/html`)}>Ver informe completo</Button>
          <button onClick={() => void api.download(`/api/portal/cases/${id}/report/pdf`, `informe-${data.case.publicRef}.pdf`)} className="text-sm text-muted hover:underline">Descargar PDF</button>
        </div>
      </Card>

      {insp.findings.some((f) => f.vehicleArea && ZONE_CODES.has(f.vehicleArea)) && (
        <Card className="mb-6 p-5">
          <div className="mb-2 text-sm font-semibold">Mapa de daños de carrocería</div>
          <VehicleDiagram findings={insp.findings.filter((f) => f.vehicleArea && ZONE_CODES.has(f.vehicleArea))} />
        </Card>
      )}

      {insp.findings.length > 0 && (
        <Card className="mb-6">
          <div className="border-b border-line px-5 py-3.5 text-sm font-semibold">Incidencias detectadas ({insp.findings.length})</div>
          <ul className="divide-y divide-line">
            {insp.findings.slice().sort((a, b) => rank(b.severity) - rank(a.severity)).map((f, i) => (
              <li key={i} className="flex items-start justify-between gap-3 px-5 py-3">
                <div><div className="text-sm font-medium">{f.vehicleArea ? `${f.vehicleArea} — ` : ""}{f.title}</div>{f.description && <div className="text-xs text-muted">{f.description}</div>}</div>
                <Badge tone={f.severity === "CRITICAL" || f.severity === "MAJOR" ? "danger" : "warn"}>{f.severity}</Badge>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="flex flex-col gap-3">
        {insp.sections.map((sec) => {
          const isOpen = open === sec.key;
          const notable = sec.checks.filter((c) => c.status === "DEFECT" || c.status === "OBSERVATION");
          const rest = sec.checks.filter((c) => c.status !== "DEFECT" && c.status !== "OBSERVATION" && c.status !== "NOT_CHECKED");
          return (
            <Card key={sec.key}>
              <button onClick={() => setOpen(isOpen ? null : sec.key)} className="flex w-full items-center justify-between px-5 py-3.5 text-left">
                <span className="text-sm font-semibold">{sec.title}</span>
                <span className="flex items-center gap-2 text-xs">
                  {sec.counts.defect > 0 && <Badge tone="danger">{sec.counts.defect} def</Badge>}
                  {sec.counts.observation > 0 && <Badge tone="warn">{sec.counts.observation} obs</Badge>}
                  <Badge tone="ok">{sec.counts.ok} OK</Badge>
                  <span className="text-muted">{isOpen ? "▲" : "▼"}</span>
                </span>
              </button>
              {isOpen && (
                <div className="border-t border-line px-5 py-3">
                  {[...notable, ...rest].map((c) => {
                    const media = mediaByCheck.get(c.key) ?? [];
                    return (
                      <div key={c.key} className="border-b border-line py-2 last:border-0">
                        <div className="flex items-center justify-between">
                          <span className="text-sm">{c.label}{valueStr(c) ? ` — ${valueStr(c)}` : ""}</span>
                          <Badge tone={c.status === "DEFECT" ? "danger" : c.status === "OBSERVATION" ? "warn" : c.status === "OK" ? "ok" : "neutral"}>{ST_LABEL[c.status]}</Badge>
                        </div>
                        {c.comment && <div className="mt-0.5 text-xs text-muted">{c.comment}</div>}
                        {media.length > 0 && (
                          <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4">
                            {media.map((m) => <AuthImg key={m.id} src={`/api/portal/inspection-media/${m.id}/content`} alt={c.label} />)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {data.signature && <p className="mt-6 text-xs text-muted">Firmado por {data.signature.signerName} · {new Date(data.signature.signedAt).toLocaleString("es-ES")}</p>}
    </>
  );
}

function rank(s: Severity): number { return { INFO: 0, MINOR: 1, MAJOR: 2, CRITICAL: 3 }[s]; }
